import { useState } from 'react';

import {
  IconAlertTriangleFilled,
  IconCertificate,
  IconDeviceGamepad2,
  IconEdit,
  IconLink,
  IconNote,
  IconSwitch,
  IconTrash,
} from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { ContentStep } from '@cfa/pathway-fe-shared/xp';
import { IconButton, Typography } from '@cfa/react-components';

import { useAuth } from '@/hooks/useAuth';

import {
  getStepAvailable,
  getStepNote,
  getStepResource,
  getStepUrls,
} from '@/utils/contentStep';
import { getPlanStepResourceReferenceName } from '@/utils/stepName';
import { cn } from '@/utils/tailwind';
import { getClickableLink } from '@/utils/url';

import { BaseReferenceDTO } from '@/services/content-api-types';
import { LanguageObject, TaskProps } from '@/types/types';

import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import Constants from 'constants/index';
import { isValidUrl } from 'utils/isValidUrl';
import { getNameFromLanguage } from 'utils/language';

import EditButtons from './EditButtons';
import EditTask from './EditTask';
import ResourceName from './ResourceName';
import SixDotIcon from './SixDotIcon';

import { sessionOverridesAtom } from '@/atoms/sessionOverrides';

export interface SectionFunctionProps {
  id: string;
  name: string;
  sectionId: string;
  type: string;
}

interface PlanStepContainerProps {
  className?: string;
  isAllowedToEdit?: boolean;
  isPreview?: boolean;
  onCancel?: () => void;
  onDelete?: (section: SectionFunctionProps) => void;
  onEdit?: (
    type: string,
    title: string | null,
    task: TaskProps,
    id: string,
  ) => void;
  sectionId?: string;
  step: ContentStep;
  taskUpdated?: boolean;
  language?: string;
  handleResourceOrQuizPreview?: (document: BaseReferenceDTO | string) => void;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  handleRef?: React.Ref<HTMLElement>;
  isReadOnly?: boolean;
}

const PlanStepContainer: React.FC<PlanStepContainerProps> = ({
  className,
  isAllowedToEdit,
  isPreview,
  onCancel,
  onDelete,
  onEdit,
  sectionId,
  step,
  taskUpdated,
  language,
  handleResourceOrQuizPreview,
  editProcedureResource,
  handleRef,
  isReadOnly = false,
}) => {
  const { data: auth } = useAuth();

  const userIsAdminOperatorOrStakeholder = auth?.isAdminOperatorOrStakeholder;
  const sessionOverrides = useAtomValue(sessionOverridesAtom);

  const canViewSwitchButton =
    userIsAdminOperatorOrStakeholder && sessionOverrides.has('updateResource');

  const { t } = useTranslation();
  const languageCode = language ?? '';
  const resolvedSectionId = sectionId ?? '';
  const stepId = step.id;
  const stepType = step.type;
  const stepResource = getStepResource(step);
  const note = getNameFromLanguage(getStepNote(step) as LanguageObject);
  const firstUrl = getNameFromLanguage(
    getStepUrls(step)?.[0]?.urlSet as LanguageObject,
  );
  const hasNotes = !!note;
  const hasUrls = !!firstUrl;
  const isTask = step.type === Constants.STEP_TYPES.TASK;
  const isDocument = step.type === Constants.STEP_TYPES.DOCUMENT;
  const isGame = stepResource?.type?.toUpperCase() === 'GAME';
  const isQuiz = step.type === Constants.STEP_TYPES.QUIZ;
  const isBrightCoveVideo =
    step.type === Constants.STEP_TYPES.DOCUMENT &&
    stepResource?.type?.toLowerCase() ===
      Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE;

  /**
   * https://cfacorp.atlassian.net/browse/PATHWAY-2168
   * Expect quizId (ae4c9766-172f-44d9-8a6b-2965e3861844) not typeFormId (freeXPT2) here.
   * uncomment below for quiz when quizId is available in /plans/templates or /plans/:planId
   * Note: may not be "quizId" field name when add it to above endpoint response
   **/
  const quizId = false; // isQuiz && step?.quizId;

  const stepIsClickable = isDocument
    ? getStepAvailable(step)
    : hasNotes || hasUrls || quizId;

  const stepName = getNameFromLanguage(step.name as LanguageObject, language);
  const stepReferenceName = getPlanStepResourceReferenceName(
    step,
    languageCode,
  );

  const matchingResourceRef =
    stepResource?.references?.find(
      ref => ref.language.toLowerCase() === languageCode.toLowerCase(),
    ) ?? stepResource?.references?.[0];

  const primaryResourceUrl = matchingResourceRef?.urls.at(0);

  const stepReference: BaseReferenceDTO | undefined = matchingResourceRef
    ? {
        id: matchingResourceRef.id,
        name: matchingResourceRef.name,
        language: matchingResourceRef.language,
        country: matchingResourceRef.country,
        documentId: stepResource?.id,
        contentUrl: primaryResourceUrl?.contentUrl,
        url: primaryResourceUrl?.contentUrl,
        sourceSystem: matchingResourceRef.sourceSystem,
        type: stepResource?.type ?? matchingResourceRef.format,
      }
    : undefined;

  const name =
    isDocument && step.available && stepReferenceName
      ? stepReferenceName
      : stepName;

  const [task, setTask] = useState({
    id: step.id,
    name: name,
    note,
    url: firstUrl,
  });
  const [showEditTask, setShowEditTask] = useState(false);
  const [showExpandedItem, setShowExpandedItem] = useState(false);
  const [error, setError] = useState('');
  const [isResourcePreviewOpen, setIsResourcePreviewOpen] = useState(false);

  const [prevTaskUpdated, setPrevTaskUpdated] = useState(taskUpdated);
  if (prevTaskUpdated !== taskUpdated) {
    setPrevTaskUpdated(taskUpdated);
    if (taskUpdated) {
      setError('');
    }
  }

  const handleCancel = () => {
    setError('');
    onCancel?.();
    setShowEditTask(false);
    setTask({
      id: '',
      name,
      note,
      url: firstUrl,
    });
  };

  const handleOnEdit = ({
    type,
    updatedSectionTitle,
    editableTask,
    id,
  }: {
    type: string;
    updatedSectionTitle: null;
    editableTask: TaskProps;
    id: string;
  }) => {
    const editAndClearUrl = () => {
      onEdit?.(type, updatedSectionTitle, { ...editableTask, url: '' }, id);
      setTask({ ...task, url: '' });
    };

    const editAndTrimUrl = () => {
      onEdit?.(
        type,
        updatedSectionTitle,
        { ...editableTask, url: editableTask.url.trim() },
        id,
      );
      setTask({ ...task, url: task.url.trim() });
    };
    if (
      !editableTask.url ||
      isValidUrl(editableTask.url.trim()) ||
      editableTask.url.trim() === ''
    ) {
      if (editableTask.url.trim() === '') {
        editAndClearUrl();
      } else {
        editAndTrimUrl();
      }

      setShowEditTask(false);
      setError('');
    } else {
      setError(t('Input.Errors.url'));
    }
  };

  const handleItemClick = () => {
    if (isTask) {
      if (stepIsClickable && !showEditTask) {
        setShowExpandedItem(prev => !prev);
      }
      return;
    }
    if (!handleResourceOrQuizPreview) {
      return;
    }
    if (isDocument && stepReference) {
      handleResourceOrQuizPreview({
        ...stepReference,
        contentUrl: stepReference.contentUrl ?? stepReference.url,
      });
    }
    // removing while quizId is hardcoded
    // else if (quizId) {
    //   handleResourceOrQuizPreview(quizId);
    // }
  };

  const taskTitle = isBrightCoveVideo
    ? t('Generic.video')
    : isDocument && isGame
      ? t('Generic.game')
      : isDocument
        ? t('Generic.resource')
        : isQuiz
          ? t('Generic.quiz')
          : t('Generic.task');

  return (
    <div className={cn('bg-base-0 w-full', className)}>
      <div className="bg-base-0 flex w-full content-between items-center gap-4 rounded-md">
        {!isPreview && !isReadOnly && (
          <SixDotIcon
            ref={handleRef}
            position={showEditTask ? 'top' : 'center'}
          />
        )}

        <div className="contents">
          {showEditTask && (
            <div className="flex w-full flex-col items-start justify-between">
              <EditTask
                error={error}
                onEdit={() => {
                  handleOnEdit({
                    type: Constants.TRAINING_MENU_OPTIONS.TASK,
                    updatedSectionTitle: null,
                    editableTask: task,
                    id: resolvedSectionId,
                  });
                }}
                setTask={updatedTask =>
                  setTask({ ...updatedTask, id: task.id })
                }
                task={task}
              />

              <div className="flex">
                <EditButtons
                  onCancel={handleCancel}
                  onEdit={() =>
                    handleOnEdit({
                      type: Constants.TRAINING_MENU_OPTIONS.TASK,
                      updatedSectionTitle: null,
                      editableTask: task,
                      id: resolvedSectionId,
                    })
                  }
                  validated={task.name.trim().length > 0}
                />
              </div>
            </div>
          )}
          {!showEditTask && (
            <>
              <button
                className={cn(
                  'flex w-full flex-col justify-between text-left',
                  {
                    'cursor-grab': stepIsClickable,
                  },
                )}
                disabled={!stepIsClickable}
                onClick={handleItemClick}
                type="button"
              >
                <Typography
                  className="text-grayscale-gray6"
                  data-testid="TaskTitle"
                  variant="overline3"
                >
                  {taskTitle}
                </Typography>

                <div
                  className="w-full wrap-break-word break-all"
                  data-testid="TaskName"
                >
                  {isDocument && !step.available ? (
                    <div className="mt-2">
                      <Typography
                        className="wrap-break-word break-all"
                        variant="body1"
                      >
                        {/* remove underscores from name */}
                        {name.replace(/_/g, ' ')}
                      </Typography>

                      <div className="text-feedback-error-base flex items-start">
                        <IconAlertTriangleFilled
                          size={16}
                          className="my-1! mr-1! min-w-4!"
                        />

                        <Typography
                          className="text-feedback-error-base! ml-2"
                          variant="body1"
                        >
                          {t('TrainingPlans.noResourceBuild')}
                        </Typography>
                      </div>
                    </div>
                  ) : step.type === Constants.STEP_TYPES.DOCUMENT ? (
                    <ResourceName
                      step={step}
                      name={name}
                      isPreviewOpen={isResourcePreviewOpen}
                      onPreviewOpenChange={setIsResourcePreviewOpen}
                    />
                  ) : (
                    <Typography
                      className="wrap-break-word break-all"
                      variant="body1"
                    >
                      {name}
                    </Typography>
                  )}
                </div>
              </button>

              <div
                className={cn('flex items-center justify-between', {
                  'cursor-pointer': !isQuiz,
                })}
              >
                {isGame && (
                  <div className="icon-container">
                    <IconDeviceGamepad2 />
                  </div>
                )}
                {isQuiz && (
                  <div className="icon-container">
                    <IconCertificate />
                  </div>
                )}
                {hasNotes && (
                  <button
                    className="icon-container cursor-pointer"
                    data-testid="TaskNoteIcon"
                    onClick={() => setShowExpandedItem(prev => !prev)}
                    type="button"
                  >
                    <IconNote />
                  </button>
                )}
                {hasUrls && (
                  <button
                    className="icon-container cursor-pointer"
                    data-testid="TaskUrlIcon"
                    onClick={() => setShowExpandedItem(prev => !prev)}
                    type="button"
                  >
                    <IconLink />
                  </button>
                )}
                {!isPreview && isAllowedToEdit && isTask && !isReadOnly && (
                  <PopoverMenuButton data-testid="ThreeDotMenu">
                    <PopoverMenuButtonItem
                      dataTestId="TaskEdit"
                      icon={<IconEdit />}
                      onClick={() => {
                        setShowExpandedItem(false);
                        setShowEditTask(true);
                      }}
                      text={t('TrainingPlans.manageThreeDotMenu.editTask')}
                    />
                    <PopoverMenuButtonItem
                      dataTestId="ThreeDotMenuDeletePlanPopup"
                      icon={<IconTrash data-testid="DeleteTask" />}
                      isDestructive={true}
                      onClick={() =>
                        onDelete?.({
                          id: stepId,
                          name,
                          sectionId: resolvedSectionId,
                          type: stepType,
                        })
                      }
                      text={t('TrainingPlans.manageThreeDotMenu.deleteTask')}
                    />
                  </PopoverMenuButton>
                )}
                {!isTask && isAllowedToEdit && !isReadOnly && (
                  <>
                    {canViewSwitchButton && (
                      <IconButton>
                        <IconSwitch
                          data-testid="ResourceUpdateIcon"
                          onClick={() =>
                            step.id &&
                            editProcedureResource?.({
                              sectionId,
                              stepId: step.id,
                            })
                          }
                        />
                      </IconButton>
                    )}
                    <IconButton>
                      <IconTrash
                        data-testid="ResourceTrashIcon"
                        onClick={() =>
                          onDelete?.({
                            id: stepId,
                            name,
                            sectionId: resolvedSectionId,
                            type: stepType,
                          })
                        }
                      />
                    </IconButton>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showExpandedItem && isTask && (
        <div className="[&_a:hover]: flex w-full flex-col pl-9 font-normal">
          {hasNotes && (
            <div className="pt-2.5 text-left">
              <Typography variant="overline3">{t('Generic.note')}</Typography>
              <Typography data-testid="TaskNote" variant="body1">
                {note}
              </Typography>
            </div>
          )}
          {hasUrls &&
            step.urls?.map((link, idx) => (
              <a
                className="pt-2.5 text-left"
                href={getClickableLink(
                  getNameFromLanguage(link.urlSet as LanguageObject),
                )}
                key={`${JSON.stringify(link.urlSet)}-${idx}`}
                target="_blank"
                rel="noreferrer"
              >
                <Typography variant="overline3">{t('Generic.link')}</Typography>
                <Typography
                  className="[&_a:hover]:text-secondary-hover wrap-break-word underline [&_a:hover]:cursor-pointer [&_a:hover]:font-normal [&_a:hover]:underline"
                  color="secondary"
                  data-testid="TaskUrl"
                  fontWeight="extralight"
                  variant="body1"
                >
                  {getNameFromLanguage(link.urlSet as LanguageObject)}
                </Typography>
              </a>
            ))}
        </div>
      )}
    </div>
  );
};

export default PlanStepContainer;
